import { type ReactNode, useEffect } from "react";
import { useBlocker, useLocation, useNavigate } from "react-router-dom";
import { SafeSaveBar as SaveBar } from "../../app/components/SafeAppBridge";
import { useModalOpen } from "../../app/components/polaris";
import { FEATURES } from "../../app/terminology/features";
import { useLegacyMode } from "../legacy/LegacyModeContext";
import { getSettingsTabs } from "./settingsPaths";
import "./SettingsPageLayout.css";

type SaveProps = {
    dirty: boolean;
    saving: boolean;
    onSave: () => void;
    onDiscard: () => void;
    saveDisabled?: boolean;
};

type Props = {
    children: ReactNode;
    /** Pages with a form pass this to get Shopify's save bar and the leave guard. */
    form?: SaveProps;
    error?: string;
    primaryAction?: ReactNode;
    /** Full-width page, for editors with a live preview. */
    wide?: boolean;
    /** Shows a spinner below the tabs while the tab's settings load. */
    loading?: boolean;
};

// Tabs are secondary navigation within App settings. Each tab is its own page,
// so the row stays in place and only the content below it changes.
function SettingsTabs() {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { isLegacy } = useLegacyMode();
    return (
        <nav className="pf-settings-tabs" aria-label="Settings sections">
            {getSettingsTabs(isLegacy).map(tab => {
                const current = pathname === tab.path;
                return (
                    <s-button key={tab.path} href={tab.path} variant={current ? "secondary" : "tertiary"}
                        aria-current={current ? "page" : undefined}
                        onClick={event => { event.preventDefault(); if (!current) navigate(tab.path); }}>
                        {tab.label}
                    </s-button>
                );
            })}
        </nav>
    );
}

// One App settings tab: Shopify's contextual save bar and a guard against
// leaving with drafts, including for another tab.
export default function SettingsPageLayout({ children, form, error, primaryAction, wide, loading }: Props) {
    const dirty = !!form?.dirty;
    const blocker = useBlocker(({ currentLocation, nextLocation }) => dirty && currentLocation.pathname !== nextLocation.pathname);
    const leaveModalRef = useModalOpen(blocker.state === "blocked", () => { if (blocker.state === "blocked") blocker.reset(); });

    useEffect(() => {
        if (!dirty) return;
        const preventLeave = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
        window.addEventListener("beforeunload", preventLeave);
        return () => window.removeEventListener("beforeunload", preventLeave);
    }, [dirty]);

    return (
        <>
            {form && <SaveBar id="settings-page-save-bar" open={dirty}>
                <button variant="primary" onClick={form.onSave} disabled={form.saving || form.saveDisabled}
                    loading={form.saving ? "" : undefined}>Save</button>
                <button onClick={form.onDiscard} disabled={form.saving}>Discard</button>
            </SaveBar>}
            <s-page heading={FEATURES.settings} inlineSize={wide ? "large" : "base"}>
                {primaryAction}
                <SettingsTabs />
                {error && <s-banner tone="critical">{error}</s-banner>}
                {loading ? <s-section>
                    <s-stack alignItems="center" padding="base"><s-spinner accessibilityLabel="Loading settings" /></s-stack>
                </s-section> : children}
            </s-page>
            {form && <s-modal ref={leaveModalRef} id="pf-leave-settings-modal" heading="Unsaved changes">
                <s-paragraph>Save or discard your changes before leaving.</s-paragraph>
                <s-button slot="primary-action" variant="primary"
                    onClick={() => blocker.state === "blocked" && blocker.reset()}>Keep editing</s-button>
                <s-button slot="secondary-actions" tone="critical"
                    onClick={() => { if (blocker.state === "blocked") { form.onDiscard(); blocker.proceed(); } }}>Discard and leave</s-button>
            </s-modal>}
        </>
    );
}
